import { useEffect, useRef, useState } from "react";
import { Form, required, useLogin, useNotify, useTranslate } from "ra-core";
import type { SubmitHandler, FieldValues } from "react-hook-form";
import { Link, useLocation, useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import { TextInput } from "@/components/admin/text-input";
import { Notification } from "@/components/admin/notification";
import { GRAVUN_BRAND } from "@/branding";
import { SSOAuthButton } from "./SSOAuthButton";
import {
  disableEmailPasswordAuthentication,
  googleWorkplaceDomain,
} from "./authConfig";

/**
 * Login page displayed when authentication is enabled and the user is not authenticated.
 *
 * Automatically shown when an unauthenticated user tries to access a protected route.
 * Handles login via authProvider.login() and displays error notifications on failure.
 *
 * @see {@link https://marmelab.com/shadcn-admin-kit/docs/loginpage LoginPage documentation}
 * @see {@link https://marmelab.com/shadcn-admin-kit/docs/security Security documentation}
 */
export const LoginPage = (props: { redirectTo?: string }) => {
  const { redirectTo } = props;
  const [loading, setLoading] = useState(false);
  const hasDisplayedRecoveryNotification = useRef(false);
  const location = useLocation();
  const navigate = useNavigate();
  const login = useLogin();
  const notify = useNotify();
  const translate = useTranslate();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const shouldNotify = searchParams.get("passwordRecoveryEmailSent") === "1";

    if (!shouldNotify || hasDisplayedRecoveryNotification.current) {
      return;
    }

    hasDisplayedRecoveryNotification.current = true;
    notify("crm.auth.recovery_email_sent", {
      type: "success",
      messageArgs: {
        _: "If you're a registered user, you should receive a password recovery email shortly.",
      },
    });

    searchParams.delete("passwordRecoveryEmailSent");
    const nextSearch = searchParams.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : "",
      },
      { replace: true },
    );
  }, [location.pathname, location.search, navigate, notify]);

  const handleSubmit: SubmitHandler<FieldValues> = (values) => {
    setLoading(true);
    login(values, redirectTo)
      .then(() => {
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        notify(
          typeof error === "string"
            ? error
            : typeof error === "undefined" || !error.message
              ? "ra.auth.sign_in_error"
              : error.message,
          {
            type: "error",
            messageArgs: {
              _:
                typeof error === "string"
                  ? error
                  : error && error.message
                    ? error.message
                    : undefined,
            },
          },
        );
      });
  };

  return (
    <div className="min-h-screen flex">
      <div className="relative grid w-full lg:grid-cols-2">
        {/* Brand panel, matches app.gravun.com/login */}
        <div className="relative hidden h-full flex-col justify-between bg-[oklch(0.22_0.055_262)] p-12 text-white lg:flex">
          <img
            className="h-10 w-auto self-start"
            src={GRAVUN_BRAND.wordmarkWhite}
            alt={GRAVUN_BRAND.name}
          />
          <div className="max-w-md space-y-4">
            <h2 className="text-3xl font-bold leading-tight tracking-tight">
              {GRAVUN_BRAND.tagline}
            </h2>
            <p className="text-base text-white/60">
              {GRAVUN_BRAND.authSubline}
            </p>
          </div>
          <p className="text-sm text-white/40">
            © {new Date().getFullYear()} {GRAVUN_BRAND.name} Inc.
          </p>
        </div>
        {/* Form panel */}
        <div className="flex flex-col justify-center w-full bg-background p-4 lg:p-8">
          <div className="w-full lg:mx-auto lg:w-[420px]">
            <img
              className="mx-auto mb-8 h-8 w-auto lg:hidden dark:hidden"
              src={GRAVUN_BRAND.wordmarkNavy}
              alt={GRAVUN_BRAND.name}
            />
            <div className="rounded-2xl border border-border-subtle bg-card p-8 shadow-none space-y-6">
              <div className="space-y-1">
                <h1 className="text-2xl font-semibold tracking-tight">
                  {translate("crm.auth.welcome_back", { _: "Welcome back" })}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {translate("crm.auth.sign_in_subtitle", {
                    _: "Sign in to your Gravun workspace",
                  })}
                </p>
              </div>
              {disableEmailPasswordAuthentication ? null : (
                <Form className="space-y-6" onSubmit={handleSubmit}>
                  <TextInput
                    label="ra.auth.email"
                    source="email"
                    type="email"
                    validate={required()}
                  />
                  <div className="relative">
                    <TextInput
                      label="ra.auth.password"
                      source="password"
                      type="password"
                      validate={required()}
                    />
                    <Link
                      to={"/forgot-password"}
                      className="absolute right-0 top-0 text-sm text-muted-foreground hover:underline"
                    >
                      {translate("ra-supabase.auth.forgot_password", {
                        _: "Forgot password?",
                      })}
                    </Link>
                  </div>
                  <Button
                    type="submit"
                    className="w-full cursor-pointer"
                    size="lg"
                    disabled={loading}
                  >
                    {translate("ra.auth.sign_in")}
                  </Button>
                </Form>
              )}
              {googleWorkplaceDomain ? (
                <SSOAuthButton
                  className="w-full"
                  domain={googleWorkplaceDomain}
                >
                  {translate("crm.auth.sign_in_google_workspace", {
                    _: "Sign in with Google Workplace",
                  })}
                </SSOAuthButton>
              ) : null}
            </div>
          </div>
        </div>
      </div>
      <Notification />
    </div>
  );
};
